import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import {
  YOU_SPINNER_CENTER,
  YOU_SPINNER_CIRCUMFERENCE,
  YOU_SPINNER_TOKENS,
} from './spinnerTokens';

const {
  OUTER_COLOR,
  INNER_COLOR,
  INNER_SCALE,
  SPIN_MS,
  CYCLE_MS,
  VIEWBOX,
  RADIUS: R,
  DEFAULT_THICKNESS,
  DEFAULT_SIZE,
} = YOU_SPINNER_TOKENS;

const CENTER = YOU_SPINNER_CENTER;
const C = YOU_SPINNER_CIRCUMFERENCE;

const cycleCCW = keyframes`
  0% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${C};
  }
  50.01% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${-C};
  }
  100% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
`;

const cycleCW = keyframes`
  0% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${-C};
  }
  50.01% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${C};
  }
  100% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
`;

const spinCCW = keyframes`
  to {
    transform: rotate(-360deg);
  }
`;

const spinCW = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Svg = styled.svg`
  display: inline-block;
  flex-shrink: 0;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
`;

const ScaleWrapper = styled.g`
  transform-box: view-box;
  transform-origin: ${CENTER}px ${CENTER}px;
  transform: scale(${INNER_SCALE});
`;

const OuterGroup = styled.g`
  transform-box: view-box;
  transform-origin: ${CENTER}px ${CENTER}px;
  will-change: transform;
  animation: ${spinCCW} ${SPIN_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: ${SPIN_MS * 2}ms;
  }
`;

const InnerGroup = styled.g`
  transform-box: view-box;
  transform-origin: ${CENTER}px ${CENTER}px;
  will-change: transform;
  animation: ${spinCW} ${SPIN_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: ${SPIN_MS * 2}ms;
  }
`;

const ArcBase = styled.circle`
  fill: none;
  stroke-linecap: round;
  will-change: stroke-dashoffset;
`;

const OuterArc = styled(ArcBase)`
  animation: ${cycleCCW} ${CYCLE_MS}ms linear infinite;
`;

const InnerArc = styled(ArcBase)`
  animation: ${cycleCW} ${CYCLE_MS}ms linear infinite;
`;

const _toCssSize = (size) =>
  typeof size === 'number' ? `${size}px` : size || DEFAULT_SIZE;

export const YouSpinner = ({
  size,
  color,
  thickness = DEFAULT_THICKNESS,
  ...rest
}) => {
  const sw = Number.parseFloat(thickness) || DEFAULT_THICKNESS;
  return (
    <Svg
      role="status"
      aria-label="Carregando"
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      $size={_toCssSize(size)}
      {...rest}
    >
      <OuterGroup>
        <OuterArc
          cx={CENTER}
          cy={CENTER}
          r={R}
          strokeWidth={sw}
          style={{ stroke: color || OUTER_COLOR }}
        />
      </OuterGroup>
      <ScaleWrapper>
        <InnerGroup>
          <InnerArc
            cx={CENTER}
            cy={CENTER}
            r={R}
            strokeWidth={sw}
            style={{ stroke: INNER_COLOR }}
          />
        </InnerGroup>
      </ScaleWrapper>
    </Svg>
  );
};

YouSpinner.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  thickness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default YouSpinner;
